import { isString, isEmpty, isNull, toUpper, isObjectLike, isFinite } from 'lodash';
import { List } from 'immutable';
import axios from 'axios';
import config from 'commons-vehicle/config/index.js';

import { LocaleService, ConfigsService, HTTPService } from '@sm360/widget-commons-core-services';
import { CookieService, COOKIES } from '@sm360/widget-commons-general';

import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import { resetModelYearFromOriginData } from 'commons-vehicle/ModelYear/services/ModelYearService/ModelYearService.js';
import { resetTradeInVehicleFromShowroomTradeInVehicle } from 'commons-vehicle/Vehicle/services/TradeInVehicleService/TradeInVehicleService.js';

import ShowroomAPIService from 'commons-vehicle/services/ShowroomService/ShowroomAPIService.js';
import TradeInVehicleAPIService from 'commons-vehicle/Vehicle/services/TradeInVehicleAPIService/TradeInVehicleAPIService.js';
import DisclaimerAPIService from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsAPIService/DisclaimerAPIService.js';

import ModelYearModel from 'commons-vehicle/ModelYear/models/ModelYearModel.js';
import PurchaseMethodsModel from 'commons-vehicle/PurchaseMethods/models/PurchaseMethodsModel.js';
import TradeInVehicleModel from 'commons-vehicle/Vehicle/models/TradeInVehicleModel.js';
import { VEHICLE } from 'commons-vehicle/constants/vehicle.js';

/**
 * Util to merge Model Years API calls responses into a new models object and return the new merged object
 * @param {Object[]} results API response data array
 * @param {Boolean} requestTradeInVehicleLoad
 * @param {String} language
 */
function mergeLoadBuildAndQuoteModelYearsDataResults(results, requestTradeInVehicleLoad, language, disclaimers) {
    if (isEmpty(results)) {
        return {
            years: List([]),
            tradeInVehicle: new TradeInVehicleModel(),
        };
    }
    const modelYearsData = results[0].data.view;
    const modelYearsConverted = resetModelYearFromOriginData(
        DataOrigins.MODEL_YEARS,
        modelYearsData,
        isEmpty(language) ? 'en' : language,
        disclaimers
    );

    const tradeInVehicle =
        requestTradeInVehicleLoad && !isEmpty(results[1])
            ? resetTradeInVehicleFromShowroomTradeInVehicle(results[1].data)
            : new TradeInVehicleModel();

    return {
        years: modelYearsConverted.years,
        tradeInVehicle,
    };
}

/**
 * Util to merge Model Year API calls responses into a new models object and return the new merged object
 * @param {Object[]} results API response data array
 * @param {Boolean} requestTradeInVehicleLoad
 * @param {String} language
 */
function mergeLoadBuildAndQuoteModelYearDataResults(results, requestTradeInVehicleLoad, language, disclaimers) {
    if (isEmpty(results)) {
        return {
            modelYear: new ModelYearModel(),
            purchaseMethods: new PurchaseMethodsModel(),
            tradeInVehicle: new TradeInVehicleModel(),
        };
    }
    const modelYearData = results[0].data.view;
    const modelYearConverted = resetModelYearFromOriginData(
        DataOrigins.MODEL_YEAR_POWERS_TRIMS,
        modelYearData,
        isEmpty(language) ? 'en' : language,
        disclaimers
    );

    const tradeInVehicle =
        requestTradeInVehicleLoad && !isEmpty(results[1])
            ? resetTradeInVehicleFromShowroomTradeInVehicle(results[1].data)
            : new TradeInVehicleModel();

    return {
        ...modelYearConverted,
        tradeInVehicle,
    };
}

/**
 * Groups Make Model Year api calls to ShowroomAPI
 */
class ModelYearAPIService {
    constructor() {
        /**
         * The base path for Make Model Year ShowroomAPI
         * @type {string}
         */
        this.basePathModelYear = `${config.endpoint.showroom}/service/v1/view/2.1`;

        /**
         * The base path for Make Model Years ShowroomAPI
         * @type {string}
         */
        this.basePathModelYears = `${config.endpoint.showroom}/service/v1/view/1.1`;
    }

    /**
     * Returns the axios request configuration object to load a Make Model Year Model for a given model year
     * @param {string} publicToken
     * @param {int} year
     * @param {string} make
     * @param {string} model
     * @param {string} bodyStyleCode
     * @param {int} vehicleConditionType
     */
    getLoadModelYearRequestConfig(publicToken, year, make, model, bodyStyleCode, vehicleConditionType) {
        const orgId = ConfigsService.getOrganizationId();
        const orgUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const language = LocaleService.getLocale();

        const requestConfig = {
            url: this.basePathModelYear,
            method: 'get',
        };

        requestConfig.headers = {
            'x-jwt': isString(publicToken) ? publicToken : '',
        };

        requestConfig.params = {
            context: `${isNull(orgId) ? '' : orgId}.${isNull(orgUnitId) ? '' : orgUnitId}`,
            location: isEmpty(location) ? '' : location,
            language: isEmpty(language) ? '' : language,
            year: isFinite(year) ? year : 0,
            make: toUpper(isString(make) ? make : ''),
            model: isString(model) ? model : '',
            bodyStyleCode: toUpper(isString(bodyStyleCode) ? bodyStyleCode : ''),
            datalevel: 256,
            demo: vehicleConditionType && vehicleConditionType === VEHICLE.CONDITIONS.DEMO,
        };

        return requestConfig;
    }

    /**
     * Returns the axios request configuration object to load a Make Model Year Model for all years of a given model
     * @param {string} publicToken
     * @param {string} make
     * @param {string} model
     * @param {string} bodyStyleCode
     */
    getLoadModelYearsRequestConfig(publicToken, make, model, bodyStyleCode) {
        const orgId = ConfigsService.getOrganizationId();
        const orgUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const language = LocaleService.getLocale();

        const requestConfig = {
            url: this.basePathModelYears,
            method: 'get',
        };

        requestConfig.headers = {
            'x-jwt': isString(publicToken) ? publicToken : '',
        };

        requestConfig.params = {
            context: `${isNull(orgId) ? '' : orgId}.${isNull(orgUnitId) ? '' : orgUnitId}`,
            location: isEmpty(location) ? '' : location,
            language: isEmpty(language) ? '' : language,
            make: toUpper(isString(make) ? make : ''),
            model: isString(model) ? model : '',
            bodyStyleCode: toUpper(isString(bodyStyleCode) ? bodyStyleCode : ''),
            subGrouping: 'year',
            datalevel: 768,
        };

        return requestConfig;
    }

    /**
     * Returns a Model Years Models Object from model and trade in parameters
     * @param {Object} modelYearsParams
     * @param {Object} tradeInVehicleParams
     */
    loadModelYears(modelYearsParams, tradeInVehicleParams) {
        if (
            !isObjectLike(modelYearsParams) ||
            isEmpty(modelYearsParams.make) ||
            isEmpty(modelYearsParams.model) ||
            isEmpty(modelYearsParams.bodyStyleCode)
        ) {
            return Promise.reject(HTTPService.formatRejectResponse('missing or invalid parameters'));
        }

        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const requireTradeInVehicleLoad =
            (isEmpty(tradeInVehicleParams) || isEmpty(tradeInVehicleParams.tradeInVehicleId)) && !isEmpty(token);
        const language = LocaleService.getLocale();

        return ShowroomAPIService.getPublicToken().then((publicToken) => {
            return DisclaimerAPIService.getGenericDisclaimers().then((disclaimers) => {
                const promises = [];
                const { make, model, bodyStyleCode } = modelYearsParams;
                const modelYearsRequestConfig = this.getLoadModelYearsRequestConfig(
                    publicToken.data,
                    make,
                    model,
                    bodyStyleCode
                );
                promises.push(axios.request(modelYearsRequestConfig));

                if (requireTradeInVehicleLoad) {
                    const tradeInVehicleId = !isEmpty(tradeInVehicleParams)
                        ? tradeInVehicleParams.tradeInVehicleId
                        : '';
                    const tradeInVehicleRequestConfig = TradeInVehicleAPIService.getLoadTradeInVehicleRequestConfig(
                        tradeInVehicleId
                    );
                    promises.push(axios.request(tradeInVehicleRequestConfig));
                }

                return axios
                    .all(promises)
                    .then((results) =>
                        mergeLoadBuildAndQuoteModelYearsDataResults(
                            results,
                            requireTradeInVehicleLoad,
                            language,
                            disclaimers
                        )
                    )
                    .catch(() => {
                        console.error(`Unable to load Model Years Data for ${make} ${model} ${bodyStyleCode}`);
                        return Promise.reject();
                    });
            });
        });
    }

    /**
     * Returns a Model Year Models Object from model and trade in parameters
     * @param {Object} modelYearParams
     * @param {Object} tradeInVehicleParams
     */
    loadModelYear(modelYearParams, tradeInVehicleParams) {
        if (
            !isObjectLike(modelYearParams) ||
            !isFinite(modelYearParams.year) ||
            isEmpty(modelYearParams.make) ||
            isEmpty(modelYearParams.model) ||
            isEmpty(modelYearParams.bodyStyleCode)
        ) {
            return Promise.reject(HTTPService.formatRejectResponse('missing or invalid parameters'));
        }

        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const requireTradeInVehicleLoad =
            (isEmpty(tradeInVehicleParams) || isEmpty(tradeInVehicleParams.tradeInVehicleId)) && !isEmpty(token);
        const language = LocaleService.getLocale();

        return ShowroomAPIService.getPublicToken().then((publicToken) => {
            return DisclaimerAPIService.getGenericDisclaimers().then((disclaimers) => {
                const promises = [];
                const { year, make, model, bodyStyleCode, vehicleConditionType } = modelYearParams;
                if (vehicleConditionType && vehicleConditionType === VEHICLE.CONDITIONS.DEMO) {
                    const modelYearsRequestConfigWithVehicleCondition = this.getLoadModelYearRequestConfig(
                        publicToken.data,
                        year,
                        make,
                        model,
                        bodyStyleCode,
                        vehicleConditionType
                    );
                    promises.push(axios.request(modelYearsRequestConfigWithVehicleCondition));
                } else {
                    const modelYearsRequestConfig = this.getLoadModelYearRequestConfig(
                        publicToken.data,
                        year,
                        make,
                        model,
                        bodyStyleCode
                    );
                    promises.push(axios.request(modelYearsRequestConfig));
                }

                if (requireTradeInVehicleLoad) {
                    const tradeInVehicleId = !isEmpty(tradeInVehicleParams)
                        ? tradeInVehicleParams.tradeInVehicleId
                        : '';
                    const tradeInVehicleRequestConfig = TradeInVehicleAPIService.getLoadTradeInVehicleRequestConfig(
                        tradeInVehicleId
                    );
                    promises.push(axios.request(tradeInVehicleRequestConfig));
                }

                return axios
                    .all(promises)
                    .then((results) =>
                        mergeLoadBuildAndQuoteModelYearDataResults(
                            results,
                            requireTradeInVehicleLoad,
                            language,
                            disclaimers
                        )
                    )
                    .catch(() => {
                        console.error(`Unable to load Model Year Data for ${year} ${make} ${model} ${bodyStyleCode}`);
                        return Promise.reject();
                    });
            });
        });
    }
}

export { ModelYearAPIService };
export default new ModelYearAPIService();
